// Modules
import React from "react";
// @ts-ignore
import { LocalizationContext } from "react-localize";

// Styles
import "./MvSortTable.sass";

// Components
import MvLoader from "Generic/MvLoader/MvLoader";
import MvIcon from "Generic/MvIcon";

// Interfaces
import {Tip} from 'Interfaces/TipInterface';

// Constants
import {RequestStatus} from "Constants";

// Utils
import {SortDirection} from "Utils/SortContracts";
import ToolTip from "Generic/ToolTip";
import { Position } from "Generic/ToolTip/ToolTip";
import MvError from "Generic/MvError/MvError";

export type TableHeaders = {
  text: string;
  sortable: boolean;
  curSortDirection?: SortDirection;
  onSort?(): void;
  activeSort?: boolean;
  toolTip?: Tip[]
  toolTipPosition?: Position
};

export interface TableRow {
  rowKey: string;
  rowCells: {[key: string]: any};
  rowExpanded?: React.ReactNode;
}

export type Props = {
  rowKey: string;
  headers: TableHeaders[];
  rows: TableRow[];
  dataStatus: RequestStatus;
  headerSection: React.ReactNode;
  rowsExpandable?: boolean;
  tableExpandable?: boolean;
  minWidth: number;
  columnStyles?: any;
  tableExpanded?: boolean;
  hideBorder?: boolean;
  hideRowBorder?: boolean;
  apiTimeout: string;
};

interface ExpandedRow {
  rowKey: string;
  expanded: boolean;
}

type State = {
  screenWidth: number;
  tableExpanded: boolean;
  expandedRows: {[key: string]: boolean};
  allRowsCollapsed: boolean;
  isLoading: boolean;
};

class MvSortTable extends React.Component<Props, State> {
  loadingTimeoutId: NodeJS.Timeout | null = null;
  constructor(props: Props) {
    super(props);

    this.state = {
      screenWidth: 0,
      tableExpanded: props.tableExpanded ? props.tableExpanded : false,
      expandedRows: {},
      allRowsCollapsed: true,
      isLoading: props.dataStatus === RequestStatus.LOADING
    };
  }

  componentDidMount() {
    console.log("apiTimeout MvSortTable:", Number(this.props.apiTimeout));
    this.handleResize();
    window.addEventListener('resize', this.handleResize);
    this.handleLoadingTimeout();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.clearLoadingTimeout();
  }
  
  componentDidUpdate(prevProps: Props) {
    // If dataStatus has changed, reset isLoading and handle loading timeout
    if (prevProps.dataStatus !== this.props.dataStatus) {
      this.setState({ isLoading: this.props.dataStatus === RequestStatus.LOADING }, () => {
        this.handleLoadingTimeout();
      });
    }
  }

  componentWillReceiveProps(nProps: Props) {
    if (!this.state.tableExpanded && nProps.tableExpanded) {
      this.setState({ tableExpanded: true });
    }
    // Update isLoading state based on dataStatus
    const isLoading = nProps.dataStatus === RequestStatus.LOADING;

    // Clear isLoading if dataStatus is LOADED
    if (nProps.dataStatus === RequestStatus.LOADED) {
      this.setState({ isLoading: false });
    } else {
      this.handleLoadingTimeout();
    }

    this.setState({ isLoading });
  }

  handleLoadingTimeout = () => {
    // Clear existing timeout
    this.clearLoadingTimeout();

    // If the component is in loading state, set a timeout to check if still loading after Timeout
    if (this.state.isLoading) {
      this.loadingTimeoutId = setTimeout(() => {
        console.log("API_CALL_TIMEOUT: ", Number(this.props.apiTimeout));
        // If still loading after Timeout, update isLoading state
        if (this.props.dataStatus === RequestStatus.LOADING) {
          this.setState({ isLoading: false });
        }
      }, Number(this.props.apiTimeout));
    }
  };

  clearLoadingTimeout = () => {
    if (this.loadingTimeoutId) {
      clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = null;
    }
  };

  /**
   * fired when the browser is resized
   */
  handleResize = () => {
    this.setState({ screenWidth: window.innerWidth });
  }

  /**
   * fired when the expand table button is clicked
   */
  toggleTable = () => {
    const tableExpanded = !this.state.tableExpanded;
    this.setState({ tableExpanded });
  }

  /**
   * fired when the expand icon for all rows (top left) is clicked
   */
  toggleAllRows = () => {
    // if all rows are collapsed, then open all (set to true), else collapse all
    // ie. if true, set all to expand: true
    const shouldExpand = this.allRowsCollapsed(this.state.expandedRows);
    const expandedRows: {[key: string]: boolean} = {};

    this.props.rows.forEach((row) => {
     expandedRows[row.rowKey] = shouldExpand;
    });

    this.setState({ expandedRows, allRowsCollapsed: !shouldExpand });
  }

  /**
   * check if all rows are collapsed
   */
  allRowsCollapsed = (rows: {[key: string]: boolean}):boolean => {
    return !Object.values(rows).some(expanded => expanded);
  }

  /**
   * fired when a row's expand icon is clicked
   */
  toggleExpandRow = ({rowKey, expanded}: ExpandedRow) => {
    const expandedRows = {...this.state.expandedRows};

    // set the row's expanded state
    expandedRows[rowKey] = expanded;

    const allRowsCollapsed = this.allRowsCollapsed(expandedRows);

    this.setState({
      expandedRows,
      allRowsCollapsed
    });
  }

  render() {
    const { localize } = this.context;

    const {
      rowKey,
      headers,
      rows,
      headerSection,
      rowsExpandable,
      tableExpandable,
      minWidth,
      dataStatus,
      columnStyles,
      hideBorder,
      hideRowBorder
    } = this.props;

    const {
      tableExpanded,
      screenWidth,
      expandedRows,
      allRowsCollapsed,
    } = this.state;

    // const isLoading = dataStatus === RequestStatus.LOADING;

    const showTable = tableExpanded || !tableExpandable;
    const numberText = [ "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen", "twenty", ];

    return (  
      <section className={`mv-sort-table ${hideBorder ? '' : 'mv-sort-table-border'} ${showTable ? "table-show" : "table-hide"}`}>
        {(tableExpandable || headerSection) &&
          <div className="table-header-section">
            {tableExpandable &&
              <button className="expand-icon" onClick={this.toggleTable}>
                <MvIcon
                  color="#ffffff"
                  style={{
                    height: "10px",
                    width: "10px"
                  }}
                  icon={tableExpanded ? "minus" : "plus"}
                />
              </button>
            }
            {headerSection}
          </div>
        }

        {showTable &&
          <div className="scroll-relative-container">
            <div className="scroll-outer">  {/* put overflow in the sass file */}
              <div className="scroll-inner" style={{minWidth}}>
                <div className="mv-table">
                  <div className='table-row col-headers'>
                    {rowsExpandable === true &&
                      <div className="table-header expand-icon-cell">
                        <button
                          className="expand-icon"
                          onClick={this.toggleAllRows}
                        >
                          <MvIcon
                            color="#ffffff"
                            style={{
                              height: "10px",
                              width: "10px"
                            }}
                            icon={allRowsCollapsed ? "plus" : "minus"}
                          />
                        </button>
                      </div>
                    }
                    {headers.map((header, colIdx) => {
                      const headerStyle = (columnStyles &&
                        columnStyles[colIdx] !== undefined) ?
                        columnStyles[colIdx] : {};
                      const divColNumClassName = numberText[colIdx];

                      return (
                        <div
                        className={`table-header ${divColNumClassName}`}
                        key={header.text}
                        style={headerStyle}
                        >
                          {header.text}
                          {header.toolTip &&
                            <div className='mv-tableheader-tooltip'>
                            <ToolTip
                              useRefToPosition={false}
                              tip={header.toolTip}
                              tabIndex={0}
                              id={`mv-tableheader-tooltip-${header.text}`}
                              position={header.toolTipPosition}
                            >
                              <span className='question-icon'>{"?"}</span>
                            </ToolTip>
                          </div>
                          }
                          {header.sortable &&
                            <span
                              className="sort-icon"
                              onClick={header.onSort}
                            >
                              <MvIcon
                                color={header.activeSort || header.activeSort === undefined ? "#009587" : "black"}
                                style={{
                                  height: "14px",
                                  width: "14px"
                                }}
                                icon={
                                  header.curSortDirection === SortDirection.ASC ?
                                    "sortDown" : "sortUp"
                                }
                              />
                            </span>
                          }
                        </div>
                      );
                    })}
                  </div>
                  <div className={`loader-container ${this.state.isLoading ? "loading" : ""}`}>
                    <MvLoader show={this.state.isLoading}>
                    </MvLoader>
                  </div>
                  {dataStatus === RequestStatus.ERRORED &&
                    <MvError message={localize('static.content.pwas-search-fatal-error-message')}/>
                  }
                  {!this.state.isLoading && this.props.dataStatus===RequestStatus.LOADING &&
                      <MvError message={localize('static.content.calendar-not-loading-error-message')}/>
                  }
                  {!this.state.isLoading && this.props.dataStatus !== RequestStatus.LOADING &&
                    <div className="table-rows">
                    {rows.map((row) => {

                      const rowExpanded = expandedRows[row.rowKey] === true;

                      return (
                        <div
                          className={`${hideRowBorder ? '' : 'table-row-border'} table-row`}
                          key={row.rowKey}
                        >
                          {rowsExpandable === true &&
                            <div className="table-data expand-icon-cell">
                              <button
                                className="expand-icon"
                                onClick={() => this.toggleExpandRow({
                                  rowKey: row.rowKey,
                                  expanded: !rowExpanded,
                                })}
                              >
                                <MvIcon
                                  color="#ffffff"
                                  style={{
                                    height: "10px",
                                    width: "10px"
                                  }}
                                  icon={rowExpanded ? "minus" : "plus"}
                                />
                              </button>
                            </div>
                          }
                          <div className='row-contents-container'>
                            {Object.keys(row.rowCells).map((cellDataKey, cellIndex) => {
                              const cellStyle = (columnStyles &&
                              columnStyles[cellIndex] !== undefined) ?
                                columnStyles[cellIndex] : {};
                              const divColNumClassName = numberText[cellIndex];

                              return (
                                <div
                                  className={`table-data ${divColNumClassName}`}
                                  key={`${row.rowKey}-${cellIndex}`}
                                  style={cellStyle}
                                >
                                  {row.rowCells[cellDataKey]}
                                </div>
                              );
                            })}
                            {rowsExpandable &&
                              <div
                                className={`row-expanded-container ${rowExpanded ? 'expanded' : ''}`}
                              >
                                {row.rowExpanded}
                              </div>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  }
                </div>
              </div>
            </div>
          </div>
        }
      </section>
    );
  }
}

MvSortTable.contextType = LocalizationContext;
export {
  MvSortTable,
};
